import { Num } from "@/components/Num";
import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, errorText } from "@/lib/api";
import { fmtPct } from "@/lib/format";
import { toast } from "sonner";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const ConsolidationLines = ({ year }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!year) return;
    api.get("/consolidation/lines", { params: { year } }).then((r) => setData(r.data)).catch((e) => toast.error(errorText(e)));
  }, [year]);
  if (!data || !data.sheets.length) return null;
  const props = data.properties;
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="consolidation-lines">
      <div className="px-4 pt-4">
        <h3 className="font-heading text-base font-semibold text-slate-900">Group roll-up by USALI line item</h3>
        <p className="text-[11px] text-slate-500 mt-1">Line structure follows {data.template_property}; lines are matched across properties by sheet and account description. Ratios and averages (ADR, occupancy, %) are not summed.</p>
      </div>
      <Tabs defaultValue={slug(data.sheets[0].name)}>
        <TabsList className="flex-wrap h-auto justify-start bg-slate-100 m-4 mb-0">
          {data.sheets.map((s) => <TabsTrigger key={s.name} value={slug(s.name)} data-testid={`consolidation-sheet-tab-${slug(s.name)}`} className="text-xs data-[state=active]:bg-slate-900 data-[state=active]:text-white">{s.name}</TabsTrigger>)}
        </TabsList>
        {data.sheets.map((s) => (
          <TabsContent key={s.name} value={slug(s.name)} className="mt-3">
            <div className="overflow-auto max-h-[65vh]">
              <table className="budget-table min-w-full text-xs">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="sticky-col !bg-slate-900 text-left px-3 py-2 min-w-[240px] text-[11px] uppercase tracking-wider">Line item</th>
                    {props.map((p) => <th key={p} className="px-3 py-2 text-right text-[11px] font-medium">{p}<br /><span className="text-slate-400 font-normal">Budget {year}</span></th>)}
                    <th className="px-3 py-2 text-right text-[11px] bg-emerald-800">Group FY Est</th>
                    <th className="px-3 py-2 text-right text-[11px] bg-emerald-900">Group Budget {year}</th>
                    <th className="px-3 py-2 text-right text-[11px] bg-emerald-900">Var %</th>
                  </tr>
                </thead>
                <tbody>
                  {s.lines.map((l) => l.row_type === "header" ? (
                    <tr key={l.id} className="bg-slate-50"><td className="sticky-col !bg-slate-50 px-3 py-2 text-xs font-semibold text-emerald-700 uppercase tracking-wider">{l.label}</td><td colSpan={props.length + 3} className="bg-slate-50" /></tr>
                  ) : (
                    <tr key={l.id} className={`border-b border-slate-100 hover:bg-slate-50/80 ${l.row_type !== "line" ? "font-semibold bg-slate-50/60" : ""}`} data-testid={`consolidation-line-${l.id}`}>
                      <td className="sticky-col px-3 py-1.5 text-slate-900" style={{ paddingLeft: `${12 + Math.min(l.indent, 8) * 4}px` }}>{l.label}</td>
                      {props.map((p) => <td key={p} className="px-3 py-1.5 text-right font-mono text-slate-700"><Num v={l.by_property[p]?.budget_total} line={l} /></td>)}
                      <td className="px-3 py-1.5 text-right font-mono bg-emerald-50/30">{l.summable ? <Num v={l.fy_total} line={l} /> : "·"}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-semibold bg-emerald-50/40">{l.summable ? <Num v={l.budget_total} line={l} /> : "·"}</td>
                      <td className={`px-3 py-1.5 text-right font-mono bg-emerald-50/40 ${(l.var_pct || 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{l.summable ? fmtPct(l.var_pct) : "·"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
